/**
 * SSoT for the boot-spinner markup. The vite configs insert it into the frame template & web's index.html @ build time.
 * The React components inject it with dangerouslySetInnerHTML.
 * Must keep 0 imports/dependencies so that the vite configs can import it. Styling lives in boot-splash.css.
 */

/** Animation fallbacks for wherever the ARP --loading-orbit-duration token is absent (the host).
 * The frame's sync script falls back to the same numbers, so both sides derive identical
 * wall-clock anchors. The multiplier is the chase period in cycles. boot-splash.css has its own
 * copies (3s / 3) that must stay in sync. */
export const BOOT_ORBIT_FALLBACK_MS = 3000
export const BOOT_LAP_MULTIPLIER = 3

/** Builds a document-unique mask id from any per-instance string (React's useId works as-is).
 * Strips everything that isn't id-safe, which also keeps the interpolation markup-safe —
 * useId's colons would misparse inside url(). */
const maskIdFrom = (instanceId: string): string =>
  `doss-boot-mask-${instanceId.replace(/[^A-Za-z0-9_-]/g, '')}`

/** The track the light windows dash along, clockwise from run A's tail tip. Its corners are
 * quarter arcs centered on the ring's inner corners, so a dash end in a corner is a radial cut
 * that pivots on the inner corner.
 *
 * With the 50-wide mask stroke, r = 24 reaches the ring's outer corners and folds just 1 past
 * the arc centers; folding further skews the cut. No pathLength: Chrome misplaces dash ends on
 * arcs under it. */
const maskTrack = (className: string): string =>
  `<path class="${className}" d="M 8 80 V 32 A 24 24 0 0 1 32 8 H 80 A 24 24 0 0 1 104 32 ` +
  `V 80 A 24 24 0 0 1 80 104 H 32 A 24 24 0 0 1 8 80 Z"/>`

/** The spinner's moving parts: the square ring, masked by the two light windows. Goes inside an
 * element carrying .doss-boot-splash-strip. */
const bootStripContents = (maskId: string): string =>
  `<defs><mask id="${maskId}" maskUnits="userSpaceOnUse" x="-8" y="-8" width="128" height="128">` +
  maskTrack('doss-boot-mask-run doss-boot-mask-run-a') +
  maskTrack('doss-boot-mask-run doss-boot-mask-run-b') +
  `</mask></defs>` +
  `<g mask="url(#${maskId})"><path class="doss-boot-strip-core" d="M 16 16 H 96 V 96 H 16 Z"/></g>`

/** The standalone spinner: a sized box holding the strip svg. The strip class sits on an inner
 * group, matching the wordmark. */
const spinnerHtml = (maskId: string): string =>
  `<div class="doss-boot-splash-spinner">` +
  `<svg viewBox="0 0 112 112">` +
  `<g class="doss-boot-splash-strip">${bootStripContents(maskId)}</g>` +
  `</svg>` +
  `</div>`

export const bootSpinnerHtml = (instanceId: string): string => spinnerHtml(maskIdFrom(instanceId))

/** The frame document's splash: static markup inside #root, painted from HTML parse until the
 * guest's first React commit clears it. One splash per document, so the mask id is fixed. */
export const BOOT_FRAME_SPLASH_HTML =
  `<div id="doss-boot-splash" class="doss-boot-splash" aria-hidden="true">` +
  spinnerHtml('doss-boot-mask') +
  `</div>`

/** The animated wordmark: the letterforms with the spinner as the glyph, at the loading
 * presentation (fixed #000, 116x28). Path data comes from web's wordmark-paths.ts and is
 * allowlist-stripped like the mask id. The glyph shares the spinner's 7-grid, so the 0.518
 * scale (58.014/112) is pixel-identical to the static glyph at rest. */
export const bootWordmarkSvg = (
  instanceId: string,
  wordmark: { viewBox: string; letterPaths: readonly string[] },
): string =>
  `<svg fill="none" height="28" viewBox="${wordmark.viewBox.replace(/[^\d .-]/g, '')}" width="116" xmlns="http://www.w3.org/2000/svg">` +
  wordmark.letterPaths
    .map((d) => `<path d="${d.replace(/[^MmLlHhVvCcSsQqTtAaZz0-9 .,-]/g, '')}" fill="#000"/>`)
    .join('') +
  `<g class="doss-boot-splash-strip" transform="translate(0 0.9863) scale(0.518)">` +
  bootStripContents(maskIdFrom(instanceId)) +
  `</g>` +
  `</svg>`

/** The frame's phase-anchor script, inlined into the template's head. Sets the same wall-clock
 * delays DynamicAppBootup derives, so the spinner keeps its phase across the handoff.
 * Reads the orbit duration from the inlined ARP token so a token change cannot desynchronize it. */
export const BOOT_PHASE_SYNC_SCRIPT = `;(() => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--loading-orbit-duration')
    .trim()
  const orbitMs = (raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000) || ${String(BOOT_ORBIT_FALLBACK_MS)}
  const now = Date.now()
  const root = document.documentElement.style
  root.setProperty('--doss-boot-sync-after', -(now % orbitMs) + 'ms')
  root.setProperty('--doss-boot-sync-lap', -(now % (orbitMs * ${String(BOOT_LAP_MULTIPLIER)})) + 'ms')
})()`
